import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Locale = "it" | "en" | "es";

export const LOCALES: Locale[] = ["it", "en", "es"];

const STORAGE_KEY = "vsid.locale";

export const STAGE_LABEL: Record<Locale, Record<string, string>> = {
  it: {
    registered: "Progetto registrato",
    materials_verified: "Materiali e applicatore verificati",
    substrate: "Preparazione del supporto",
    application: "Applicazione",
    finish_curing: "Finitura protettiva e maturazione",
    handover: "Collaudo e consegna",
  },
  en: {
    registered: "Project registered",
    materials_verified: "Materials & applicator verified",
    substrate: "Substrate preparation",
    application: "Application",
    finish_curing: "Protective finish & curing",
    handover: "Final inspection & handover",
  },
  es: {
    registered: "Proyecto registrado",
    materials_verified: "Materiales y aplicador verificados",
    substrate: "Preparación del soporte",
    application: "Aplicación",
    finish_curing: "Acabado protector y curado",
    handover: "Inspección final y entrega",
  },
};

export const STATUS_LABEL: Record<Locale, Record<string, string>> = {
  it: {
    pending: "In attesa",
    in_progress: "In corso",
    reported: "Lavoro dichiarato",
    approved: "Approvato",
    issue: "Anomalia aperta",
    planned: "Pianificato",
    collaudo: "Collaudo",
    certified: "Certificato",
    closed: "Chiuso",
    installed: "Installato",
    collaudo_passed: "Collaudo superato",
    revoked: "Revocato",
    ok: "OK",
    today: "Oggi",
  },
  en: {
    pending: "Pending",
    in_progress: "In progress",
    reported: "Work reported",
    approved: "Approved",
    issue: "Open issue",
    planned: "Planned",
    collaudo: "Inspection",
    certified: "Certified",
    closed: "Closed",
    installed: "Installed",
    collaudo_passed: "Inspection passed",
    revoked: "Revoked",
    ok: "OK",
    today: "Today",
  },
  es: {
    pending: "Pendiente",
    in_progress: "En curso",
    reported: "Trabajo declarado",
    approved: "Aprobado",
    issue: "Incidencia abierta",
    planned: "Planificado",
    collaudo: "Inspección",
    certified: "Certificado",
    closed: "Cerrado",
    installed: "Instalado",
    collaudo_passed: "Inspección superada",
    revoked: "Revocado",
    ok: "OK",
    today: "Hoy",
  },
};

export const WARRANTY_LABEL: Record<Locale, Record<string, string>> = {
  it: {
    adhesion: "Adesione al supporto",
    anti_crack: "Anti-fessurazione",
    uv: "Protezione UV",
    waterproof: "Impermeabilità",
  },
  en: {
    adhesion: "Adhesion to substrate",
    anti_crack: "Anti-cracking",
    uv: "UV protection",
    waterproof: "Waterproofing",
  },
  es: {
    adhesion: "Adhesión al soporte",
    anti_crack: "Anti-fisuración",
    uv: "Protección UV",
    waterproof: "Impermeabilidad",
  },
};

export const UI = {
  it: {
    orders: "Ordini",
    groupSales: "Vendite",
    newOrder: "Nuovo ordine",
    areaCalc: "Calcolo superfici",
    projects: "Cantieri",
    interventions: "Interventi",
    architects: "Architetti",
    academy: "Academy",
    toolkit: "Toolkit",
    certificate: "Certificato",
    settings: "Impostazioni",
    users: "Utenti",
    groupOps: "Operatività",
    groupNetwork: "Rete",
    groupSystem: "Sistema",
    menu: "Menu",
    logout: "Esci",
    signIn: "Accedi",
    email: "Email",
    password: "Password",
    newProject: "Nuovo cantiere",
    newSurface: "Nuova superficie",
    today: "Oggi",
    dayLog: "Giornale di cantiere",
    surfaces: "Superfici",
    stages: "Fasi di lavoro",
    progress: "Avanzamento",
    photos: "Foto",
    upload: "Carica foto",
    photo: "Foto",
    video: "Video",
    library: "Galleria",
    uploading: "Caricamento",
    report: "Dichiara completata",
    approve: "Approva",
    raiseIssue: "Segnala anomalia",
    notes: "Note",
    back: "Indietro",
    workReported: "Lavoro dichiarato",
    reviewApproved: "Revisione approvata",
    notAvailable: "Solo admin",
  },
  en: {
    orders: "Orders",
    groupSales: "Sales",
    newOrder: "New order",
    areaCalc: "Area calculator",
    projects: "Projects",
    interventions: "Interventions",
    architects: "Architects",
    academy: "Academy",
    toolkit: "Toolkit",
    certificate: "Certificate",
    settings: "Settings",
    users: "Users",
    groupOps: "Operations",
    groupNetwork: "Network",
    groupSystem: "System",
    menu: "Menu",
    logout: "Sign out",
    signIn: "Sign in",
    email: "Email",
    password: "Password",
    newProject: "New project",
    newSurface: "New surface",
    today: "Today",
    dayLog: "Site day log",
    surfaces: "Surfaces",
    stages: "Work stages",
    progress: "Progress",
    photos: "Photos",
    upload: "Upload photo",
    photo: "Photo",
    video: "Video",
    library: "Library",
    uploading: "Uploading",
    report: "Report complete",
    approve: "Approve",
    raiseIssue: "Raise issue",
    notes: "Notes",
    back: "Back",
    workReported: "Work reported",
    reviewApproved: "Review approved",
    notAvailable: "Admin only",
  },
  es: {
    orders: "Pedidos",
    groupSales: "Ventas",
    newOrder: "Nuevo pedido",
    areaCalc: "Cálculo de superficies",
    projects: "Obras",
    interventions: "Intervenciones",
    architects: "Arquitectos",
    academy: "Academy",
    toolkit: "Toolkit",
    certificate: "Certificado",
    settings: "Configuración",
    users: "Usuarios",
    groupOps: "Operativa",
    groupNetwork: "Red",
    groupSystem: "Sistema",
    menu: "Menú",
    logout: "Salir",
    signIn: "Entrar",
    email: "Email",
    password: "Contraseña",
    newProject: "Nueva obra",
    newSurface: "Nueva superficie",
    today: "Hoy",
    dayLog: "Diario de obra",
    surfaces: "Superficies",
    stages: "Fases de trabajo",
    progress: "Avance",
    photos: "Fotos",
    upload: "Subir foto",
    photo: "Foto",
    video: "Vídeo",
    library: "Galería",
    uploading: "Subiendo",
    report: "Declarar completada",
    approve: "Aprobar",
    raiseIssue: "Señalar incidencia",
    notes: "Notas",
    back: "Atrás",
    workReported: "Trabajo declarado",
    reviewApproved: "Revisión aprobada",
    notAvailable: "Solo administrador",
  },
} as const;

/** Every locale must define the same keys, so `t` can be typed precisely. */
export type UiText = Record<keyof typeof UI.it, string>;

interface LocaleValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: UiText;
}

const Ctx = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, set] = useState<Locale>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "en" || stored === "es" ? stored : "it";
  });

  const setLocale = useCallback((l: Locale) => {
    localStorage.setItem(STORAGE_KEY, l);
    set(l);
  }, []);

  const value = useMemo<LocaleValue>(
    () => ({ locale, setLocale, t: UI[locale] as UiText }),
    [locale, setLocale],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocale(): LocaleValue {
  const value = useContext(Ctx);
  if (!value) throw new Error("useLocale outside LocaleProvider");
  return value;
}
